import { Nullable } from '@orcar/common';
import {
  DriverOptionType,
  getHMFromNumber,
  getRentalTimeValue,
  getTimeOptions,
  SearchParamsType,
} from '@orcar/common-d2c';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { DRIVER_OPTIONS } from '@/constants/searchOptions';

const TIME_OPTIONS = getTimeOptions();

const useSearchOptions = (
  onSubmit: (params: SearchParamsType) => void,
  initialSearchOptions: SearchParamsType,
  onModalClose?: (option?: { onRouterBack?: boolean }) => void,
) => {
  const [pickUpDate, setPickUpDate] = useState<Dayjs>(
    dayjs(initialSearchOptions?.pickUpAt),
  );
  const [dropOffDate, setDropOffDate] = useState<Dayjs>(
    dayjs(initialSearchOptions?.pickUpAt),
  );
  const [pickUpTimeValue, setPickUpTimeValue] = useState(TIME_OPTIONS[0].value);
  const [dropOffTimeValue, setDropOffTimeValue] = useState(
    TIME_OPTIONS[0].value,
  );
  const [driverAge, setDriverAge] = useState<Nullable<DriverOptionType>>({
    key: 'gteTwentySix',
    value: null,
    text: '만 26세 이상',
  });
  const [drivingExperience, setDrivingExperience] = useState<
    Nullable<DriverOptionType>
  >({
    key: 'gteTwo',
    value: null,
    text: '2년 이상',
  });
  const [numberOfPeople, setNumberOfPeople] = useState<
    Nullable<DriverOptionType>
  >({
    key: 'notNow',
    value: null,
    text: '지금 선택 안 함',
  });

  const [searchOptionIdx, setSearchOptionIdx] = useState(0);
  const [isNextBtnDisabled, setIsNextBtnDisabled] = useState(true);
  const [isCheckTimeSelected, setIsCheckTimeSelected] = useState(true);

  const searchOptions = {
    pickUpDate,
    setPickUpDate,
    dropOffDate,
    setDropOffDate,
    pickUpTimeValue,
    setPickUpTimeValue,
    dropOffTimeValue,
    setDropOffTimeValue,
    driverAge,
    setDriverAge,
    drivingExperience,
    setDrivingExperience,
    numberOfPeople,
    setNumberOfPeople,
  };

  const handleRentalDate = () => {
    const pickTimeValue = getHMFromNumber(pickUpTimeValue);
    const dropTimeValue = getHMFromNumber(dropOffTimeValue);

    onSubmit({
      ...initialSearchOptions,
      pickUpAt: pickUpDate
        ?.set('hour', pickTimeValue.hh)
        .set('minute', pickTimeValue.mm)
        .format(),
      dropOffAt: dropOffDate
        ?.set('hour', dropTimeValue.hh)
        .set('minute', dropTimeValue.mm)
        .format(),
    });
  };

  const handleDriverInfo = () => {
    onSubmit({
      ...initialSearchOptions,
      driverAge: driverAge?.value,
      drivingExperience: drivingExperience?.value,
      numberOfPeople: numberOfPeople?.value,
    });
  };

  const handleNextButton = () => {
    if (isNextBtnDisabled || !onModalClose) return;

    if (searchOptionIdx === 2) {
      const pickTimeValue = getHMFromNumber(pickUpTimeValue);
      const dropTimeValue = getHMFromNumber(dropOffTimeValue);

      onSubmit({
        pickUpAt: pickUpDate
          ?.set('hour', pickTimeValue.hh)
          .set('minute', pickTimeValue.mm)
          .format(),
        dropOffAt: dropOffDate
          ?.set('hour', dropTimeValue.hh)
          .set('minute', dropTimeValue.mm)
          .format(),
        driverAge: driverAge?.value,
        numberOfPeople: numberOfPeople?.value,
        drivingExperience: drivingExperience?.value,
      });
      onModalClose({ onRouterBack: false });
    } else if (searchOptionIdx === 1) {
      setSearchOptionIdx(searchOptionIdx + 1);
      setIsCheckTimeSelected(true);
    } else {
      setSearchOptionIdx(searchOptionIdx + 1);
    }
  };

  const handleTabButton = (idx: number) => {
    if ((!pickUpDate || !dropOffDate) && idx > 0) return;
    if (!isCheckTimeSelected && idx > 1) return;
    setSearchOptionIdx(idx);
  };

  useEffect(() => {
    if (searchOptionIdx !== 0) return;
    if (pickUpDate && dropOffDate) {
      setIsNextBtnDisabled(false);
    } else setIsNextBtnDisabled(true);
  }, [searchOptionIdx, pickUpDate, dropOffDate]);

  useEffect(() => {
    if (searchOptionIdx !== 1) return;
    else {
      setIsCheckTimeSelected(true);
    }
  }, [searchOptionIdx, pickUpTimeValue, dropOffTimeValue, isCheckTimeSelected]);

  useEffect(() => {
    if (searchOptionIdx !== 2) return;
    if (driverAge && numberOfPeople && drivingExperience) {
      setIsNextBtnDisabled(false);
    } else setIsNextBtnDisabled(true);
  }, [searchOptionIdx, driverAge, numberOfPeople, drivingExperience]);

  useEffect(() => {
    setPickUpDate(dayjs(initialSearchOptions?.pickUpAt));
    setDropOffDate(dayjs(initialSearchOptions?.dropOffAt));

    setPickUpTimeValue(getRentalTimeValue(initialSearchOptions?.pickUpAt));
    setDropOffTimeValue(getRentalTimeValue(initialSearchOptions?.dropOffAt));

    setDriverAge(
      DRIVER_OPTIONS.driverAge.find(
        (age) => age.value === initialSearchOptions?.driverAge,
      ) || null,
    );
    setDrivingExperience(
      DRIVER_OPTIONS.drivingExperience.find(
        (experience) =>
          experience.value === initialSearchOptions?.drivingExperience,
      ) || null,
    );
    setNumberOfPeople(
      DRIVER_OPTIONS.numberOfPeople.find(
        (num) => num.value === initialSearchOptions?.numberOfPeople,
      ) || null,
    );
  }, [initialSearchOptions]);

  return {
    searchOptions,
    searchOptionIdx,
    setSearchOptionIdx,
    isNextBtnDisabled,
    isCheckTimeSelected,

    handleRentalDate,
    handleDriverInfo,
    handleNextButton,
    handleTabButton,
  };
};

export { useSearchOptions };
